// Repair tickets: statuses, ticket numbers, and how a ticket turns into receipt lines.
// Shared by the server and the screens so both always agree on totals.

export const REPAIR_STATUSES = ['checked_in', 'in_progress', 'waiting_parts', 'waiting_customer', 'ready', 'picked_up', 'cancelled'] as const;
export type RepairStatus = (typeof REPAIR_STATUSES)[number];

/** Statuses for repairs still in the store. */
export const OPEN_REPAIR_STATUSES: RepairStatus[] = ['checked_in', 'in_progress', 'waiting_parts', 'waiting_customer', 'ready'];

export const REPAIR_STATUS_LABELS: Record<RepairStatus, string> = {
  checked_in: 'Waiting for tech',
  in_progress: 'Being repaired',
  waiting_parts: 'Waiting for parts',
  waiting_customer: 'Needs customer OK',
  ready: 'Ready for pickup',
  picked_up: 'Picked up',
  cancelled: 'Returned, not repaired',
};

export function isRepairStatus(v: unknown): v is RepairStatus {
  return typeof v === 'string' && (REPAIR_STATUSES as readonly string[]).includes(v);
}

/** Repair tickets are numbered T-1001, T-1002, ... ("T" for ticket). */
export function ticketNumber(_prefix: string | null | undefined, n: number): string {
  return `T-${n}`;
}

/** Reads "T-1001", "t1001" (and older "WCW-REPAIR-001001" or "R1001") back into 1001. Plain numbers are left to receipts. */
export function parseTicketNumber(q: string): number | null {
  const m = /^(?:t[-\s]?|(?:[a-z]{2,6}-)?(?:repair-?|r))0*(\d{1,7})$/i.exec(q.trim());
  return m ? Number(m[1]) : null;
}

/**
 * What's being checked in: the menu at the top of the check-in. iPhone, iPad, MacBook,
 * game consoles and other phones have the website's models and prices (WCW Hub);
 * desktops and car parts are typed in and priced at the counter.
 */
export const DEVICE_TYPES = ['iphone', 'ipad', 'console', 'macbook', 'desktop', 'car', 'phone'] as const;
export type DeviceType = (typeof DEVICE_TYPES)[number];
export const DEVICE_TYPE_LABELS: Record<DeviceType, string> = {
  iphone: 'iPhone',
  ipad: 'iPad',
  console: 'Game Console',
  macbook: 'MacBook',
  desktop: 'Desktop',
  car: 'Car Part',
  phone: 'Samsung & other phones',
};

export function isDeviceType(v: unknown): v is DeviceType {
  return typeof v === 'string' && (DEVICE_TYPES as readonly string[]).includes(v);
}

/** The saved device type, or (for tickets from before it was saved) a good guess from the brand and model. */
export function deviceTypeOf(brand: string | null | undefined, model: string | null | undefined, saved?: string | null): DeviceType {
  if (isDeviceType(saved)) return saved;
  const b = (brand ?? '').trim().toLowerCase();
  const m = `${b} ${(model ?? '').toLowerCase()}`;
  if (b === 'desktop') return 'desktop';
  if (b === 'car part') return 'car';
  if (/\bipad\b/.test(m)) return 'ipad';
  if (/macbook/.test(m)) return 'macbook';
  if (/iphone/.test(m)) return 'iphone';
  if (/playstation|xbox|nintendo|\bps[345]\b|joy-con/.test(m)) return 'console';
  return 'phone';
}

/** "Apple iPhone 13", but "PlayStation 5" (not "PlayStation PlayStation 5"), and just what was typed for desktops and car parts. */
/** The device in as few words as it takes on a printout: "iPhone 14 Pro" (no "Apple"), "Samsung Galaxy S24". */
export function shortDeviceName(d: { brand: string; model: string }): string {
  return d.brand === 'Apple' ? d.model : repairDeviceName(d.brand, d.model);
}

/** The work in a few words for the technician's label: "Screen Replacement +1" (the model isn't repeated). */
export function labelWork(p: { device: { brand: string; model: string }; lines: { description: string }[]; issue: string }): string {
  if (!p.lines.length) return p.issue;
  let x = p.lines[0].description.split(' · ')[0].trim();
  for (const pre of [`${p.device.brand} ${p.device.model}`, p.device.model]) {
    if (pre.trim() && x.toLowerCase().startsWith(`${pre.toLowerCase()} `)) x = x.slice(pre.length + 1);
  }
  return `${x}${p.lines.length > 1 ? ` +${p.lines.length - 1}` : ''}`;
}

export function repairDeviceName(brand: string | null | undefined, model: string | null | undefined): string {
  const b = (brand ?? '').trim();
  const m = (model ?? '').trim();
  if (!m) return b;
  if (!b || /^(other|desktop|car part)$/i.test(b)) return m;
  if (m.toLowerCase().startsWith(b.toLowerCase()) || (/^playstation$/i.test(b) && /^ps\d/i.test(m))) return m;
  return `${b} ${m}`;
}

export interface PrecheckItem {
  key: string;
  label: string;
  /** Words for this check instead of "Works" / "Not working" / "Can't test". */
  ok?: string;
  bad?: string;
  na?: string;
}

/** Things checked at drop-off, so nobody argues later about what already didn't work. */
export const PRECHECK_ITEMS: PrecheckItem[] = [
  { key: 'power', label: 'Turns on' },
  { key: 'screen', label: 'Screen & touch' },
  { key: 'backglass', label: 'Back glass', ok: 'Not broken', bad: 'Broken', na: "Can't tell" },
  { key: 'faceid', label: 'Face ID / Touch ID' },
  { key: 'cameras', label: 'Cameras' },
  { key: 'audio', label: 'Speakers & mic' },
  { key: 'charging', label: 'Charging' },
  { key: 'buttons', label: 'Buttons' },
  { key: 'signal', label: 'Wi-Fi & signal' },
  { key: 'keyboard', label: 'Keyboard & trackpad' },
  { key: 'display', label: 'Picture on the screen / TV' },
  { key: 'disc', label: 'Disc drive' },
  { key: 'controllers', label: 'Controllers connect' },
  { key: 'ports', label: 'USB & other ports' },
  { key: 'fan', label: 'Fan & heat' },
];
export type PrecheckValue = 'ok' | 'bad' | 'na';
export const PRECHECK_LABELS: Record<PrecheckValue, string> = { ok: 'Works', bad: 'Not working', na: "Can't test" };

/** What one check's answer says, e.g. "Back glass: Broken" rather than "Not working". */
export function precheckLabel(key: string, v: PrecheckValue): string {
  const item = PRECHECK_ITEMS.find((it) => it.key === key);
  return item?.[v] ?? PRECHECK_LABELS[v];
}

/** The drop-off checks that make sense for each kind of device. */
export const PRECHECK_BY_TYPE: Record<DeviceType, string[]> = {
  iphone: ['power', 'screen', 'backglass', 'faceid', 'cameras', 'audio', 'charging', 'buttons', 'signal'],
  phone: ['power', 'screen', 'backglass', 'faceid', 'cameras', 'audio', 'charging', 'buttons', 'signal'],
  ipad: ['power', 'screen', 'faceid', 'cameras', 'audio', 'charging', 'buttons', 'signal'],
  macbook: ['power', 'screen', 'keyboard', 'cameras', 'audio', 'charging', 'ports', 'signal'],
  desktop: ['power', 'display', 'ports', 'audio', 'signal', 'fan'],
  console: ['power', 'display', 'disc', 'controllers', 'ports', 'fan'],
  car: ['power', 'screen', 'buttons', 'audio'],
};

export function precheckItemsFor(type: DeviceType): PrecheckItem[] {
  const keys = PRECHECK_BY_TYPE[type];
  return keys.map((k) => PRECHECK_ITEMS.find((it) => it.key === k)).filter((it): it is PrecheckItem => !!it);
}

/**
 * The drop-off check as it prints on the customer's receipt and the technician's ticket: every
 * check for this kind of device with what was found (null = not checked). Empty when nothing was
 * checked, so nothing prints.
 */
export function dropOffRows(
  device: { brand: string; model: string; type?: string | null },
  precheck: Record<string, PrecheckValue | string | undefined>,
): { key: string; label: string; value: PrecheckValue | null }[] {
  const items = precheckItemsFor(deviceTypeOf(device.brand, device.model, device.type ?? null));
  const keys = new Set(items.map((it) => it.key));
  // A check answered on an older ticket that isn't in this device's list still prints.
  const extra = PRECHECK_ITEMS.filter((it) => !keys.has(it.key) && precheck[it.key]);
  const rows = [...items, ...extra].map((it) => {
    const v = precheck[it.key];
    const value: PrecheckValue | null = v === 'ok' || v === 'bad' || v === 'na' ? v : null;
    return { key: it.key, label: it.label, value };
  });
  return rows.some((r) => r.value) ? rows : [];
}
export const DROP_OFF_NOTE = 'Checked before the repair, so there are no surprises later.';

/** What the technician's ticket prints on (Settings → Printers). */
export const TECH_TICKET_FORMATS = {
  both: 'Both: receipt paper and the 4-inch DYMO label',
  receipt: 'Receipt paper, 80 mm (the receipt printer)',
  'label-4': '4-inch label, 2-5/16 × 4 in (DYMO 30256)',
  label: 'Small label, 89 × 29 mm (DYMO 30252, like RepairDesk)',
} as const;
export type TechTicketFormat = keyof typeof TECH_TICKET_FORMATS;
export const isTechTicketFormat = (v: unknown): v is TechTicketFormat => typeof v === 'string' && v in TECH_TICKET_FORMATS;

/** "Left with the device: None" — the customer took everything with them. */
export const LEFT_WITH_NONE = 'None';
export const LEFT_WITH_OPTIONS = ['Case', 'SIM card', 'Charger', 'Memory card', 'Stylus'];
export const LEFT_WITH_BY_TYPE: Record<DeviceType, string[]> = {
  iphone: ['Case', 'SIM card', 'Charger'],
  phone: LEFT_WITH_OPTIONS,
  ipad: ['Case', 'Charger', 'Apple Pencil', 'Keyboard'],
  macbook: ['Case', 'Charger'],
  desktop: ['Power cable', 'Keyboard', 'Mouse', 'Monitor'],
  console: ['Controller', 'Power cable', 'HDMI cable', 'Game disc'],
  car: ['Key', 'Cables', 'Mounting parts'],
};

/** Cleans what was left with a device: "None" only when nothing else was picked. */
export function cleanLeftWith(list: string[] | null | undefined): string[] {
  const out: string[] = [];
  for (const raw of list ?? []) {
    const x = raw.trim().slice(0, 40);
    if (x && !out.some((y) => y.toLowerCase() === x.toLowerCase())) out.push(x);
  }
  const items = out.filter((x) => x.toLowerCase() !== LEFT_WITH_NONE.toLowerCase());
  return (items.length ? items : out).slice(0, 10);
}

/** How a ticket shows what was left with the device. */
export function leftWithText(list: string[]): string {
  if (!list.length) return '';
  if (list.length === 1 && list[0].toLowerCase() === LEFT_WITH_NONE.toLowerCase()) return 'None (nothing left with it)';
  return list.join(', ');
}

/** What the counter said about paying at drop-off. */
export const PAY_PLANS = ['now', 'pickup'] as const;
export type PayPlan = (typeof PAY_PLANS)[number];
export const PAY_PLAN_LABELS: Record<PayPlan, string> = { now: 'Paid at drop-off', pickup: 'Pays at pickup' };

export function isPayPlan(v: unknown): v is PayPlan {
  return typeof v === 'string' && (PAY_PLANS as readonly string[]).includes(v);
}

export interface RepairLineForCheckout {
  description: string;
  price_cents: number;
  parts_cents: number;
}

export interface CheckoutPart {
  description: string;
  priceCents: number;
  taxable: boolean;
}

/**
 * California taxes the parts used in a repair (at their retail value) but not the
 * labor, and the receipt has to show the two separately (CDTFA Regulation 1546).
 * Each repair line becomes a taxable "parts" line and a tax-free "labor" line.
 */
export function repairCheckoutParts(lines: RepairLineForCheckout[]): CheckoutPart[] {
  const out: CheckoutPart[] = [];
  for (const l of lines) {
    const price = Math.max(0, l.price_cents);
    const parts = Math.max(0, Math.min(l.parts_cents, price));
    const labor = price - parts;
    if (parts > 0) out.push({ description: `${l.description} · parts`, priceCents: parts, taxable: true });
    if (labor > 0 || parts === 0) out.push({ description: parts > 0 ? `${l.description} · labor` : l.description, priceCents: labor, taxable: false });
  }
  return out;
}

const LABOR_ONLY = /diagnos|software|data recovery|unlock|reset|transfer|set ?up|cleaning|backup/i;

/** True for services that don't put a part in the phone (diagnostics, software, unlocks). */
export function isLaborOnly(description: string): boolean {
  return LABOR_ONLY.test(description);
}

/** Parts value to tax when no part from inventory is attached: the store's usual share of the price. */
export function defaultPartsCents(description: string, priceCents: number, sharePct: number): number {
  if (isLaborOnly(description) || priceCents <= 0) return 0;
  const pct = Math.min(Math.max(Number.isFinite(sharePct) ? sharePct : 0, 0), 100);
  return Math.round((priceCents * pct) / 100);
}

/** The repair time picked at the bottom of the check-in: the three usual times, in minutes. */
export const REPAIR_TIMES = [25, 35, 60] as const;
/** For the jobs that take longer (parts to fit, boards, consoles): 2 hours, 4 hours, next day. */
export const LONGER_REPAIR_TIMES = [120, 240, 1440] as const;

/** "25-Minute Repair", "2-Hour Repair", "Next-Day Repair". */
export function repairTimeLabel(minutes: number | null | undefined): string | null {
  if (!minutes || !Number.isFinite(minutes) || minutes <= 0) return null;
  if (minutes < 120) return `${Math.round(minutes)}-Minute Repair`;
  if (minutes < 1440) return `${Math.round((minutes / 60) * 10) / 10}-Hour Repair`;
  return 'Next-Day Repair';
}

/** A repair time the server accepts: whole minutes, from 5 minutes to a week. */
export function isRepairMinutes(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= 5 && v <= 10_080;
}

const TIME_UNITS: [RegExp, number][] = [
  [/^(m|min|mins|minute|minutes)$/, 1],
  [/^(h|hr|hrs|hour|hours)$/, 60],
  [/^(d|day|days)$/, 1440],
  [/^(w|wk|wks|week|weeks)$/, 10_080],
];

/**
 * The repair time to pick for the website's estimate ("30 min", "30–60 min", "1-2 hours", "Same
 * day"): the longest it says, rounded up to the next time on the check-in (25, 35 or 60 minutes,
 * 2 hours, 4 hours, next day), so the ready-by time isn't promised too early. Null when it can't
 * be read, or it's more than a day (the counter picks those).
 */
export function repairMinutesFromEstimate(text: string | null | undefined): number | null {
  const t = (text ?? '').toLowerCase();
  let longest = 0;
  // "30-60 min": both numbers take the unit after them.
  let numbers: number[] = [];
  for (const part of t.match(/\d+(?:\.\d+)?|[a-z]+/g) ?? []) {
    if (/^\d/.test(part)) { numbers.push(Number(part)); continue; }
    const unit = TIME_UNITS.find(([re]) => re.test(part));
    if (unit && numbers.length) {
      longest = Math.max(longest, ...numbers.map((n) => n * unit[1]));
      numbers = [];
    }
  }
  if (!longest) {
    if (/next[\s-]?day|overnight|tomorrow/.test(t)) longest = 1440;
    else if (/same[\s-]?day/.test(t)) longest = 240;
    else return null;
  }
  return [...REPAIR_TIMES, ...LONGER_REPAIR_TIMES].find((m) => m >= longest) ?? null;
}
